/**
 * Pick a ledger from the master — or create one there and link it.
 * @file src/web/features/admin/components/LedgerPicker.tsx
 *
 * Replaces a plain `<input list="ledgernames">` on voucher lines. A datalist
 * only SUGGESTS: it happily accepts anything typed, and VoucherEntry stores a
 * name rather than a foreign key, so a typo became a line that resolved to no
 * ledger at all. Every join is `(companyId, name)` and a miss is undefined, so
 * the amount silently disappeared from the ledger statement, the trial balance
 * and GST classification rather than erroring.
 *
 * So this commits a name only when it belongs to a real ledger. When what you
 * typed does not exist the answer is not "type it anyway" — it is to create it
 * in the master, which is what the last row of the list does. The new ledger
 * gets a group and shows up everywhere a ledger should, and the line links to
 * it.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus, Check } from 'lucide-react';
import { LedgerModal } from './LedgerModal';

export interface LedgerOption { id: string; name: string; parent: string | null }

interface Props {
  value: string;
  options: LedgerOption[];
  entityId: string | null;
  onChange: (name: string) => void;
  /** Called after a ledger is created here so the parent can refresh its list. */
  onCreated: (name: string) => void;
  className?: string;
}

export function LedgerPicker({ value, options, entityId, onChange, onCreated, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [creating, setCreating] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; below: boolean } | null>(null);

  /**
   * The list is positioned FIXED, not absolute.
   *
   * A voucher's ledger picker lives inside the modal's scrolling body, and an
   * absolutely-positioned dropdown is clipped by that `overflow-y-auto` — on
   * the last row of a long voucher you could see two or three options and no
   * way to scroll to the rest. Fixed coordinates escape every ancestor's
   * overflow. It also flips above the field when there is more room up there,
   * so the bottom row behaves like the top one.
   */
  const place = useCallback(() => {
    const el = box.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const below = spaceBelow > 220 || spaceBelow > r.top;
    setRect({
      top: below ? r.bottom + 4 : Math.max(8, r.top - 4),
      left: r.left,
      width: Math.max(r.width, 260),
      below,
    });
  }, []);

  const known = useMemo(() => new Set(options.map((o) => o.name)), [options]);
  const valid = !value || known.has(value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
    return list.slice(0, 50); // a 3,000-ledger book must not render 3,000 rows
  }, [options, query]);

  useLayoutEffect(() => { if (open) place(); }, [open, place, query]);

  useEffect(() => {
    if (!open) return;
    const on = () => place();
    window.addEventListener('scroll', on, true); // capture: any ancestor scroll
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on, true); window.removeEventListener('resize', on); };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const choose = (name: string) => { onChange(name); setOpen(false); setQuery(''); };

  const typed = query.trim();
  const exact = options.some((o) => o.name.toLowerCase() === typed.toLowerCase());
  const offerCreate = typed.length > 0 && !exact;

  const key = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[active]) choose(matches[active].name);
      else if (offerCreate) setCreating(typed);
    } else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  return (
    <div ref={box} className="relative">
      <div className="relative">
        <input
          value={open ? query : value}
          placeholder={value || 'Pick a ledger'}
          onFocus={() => { setOpen(true); setQuery(''); setActive(0); }}
          onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
          onKeyDown={key}
          aria-invalid={!valid}
          className={`${className ?? ''} pr-7 ${valid ? '' : 'border-danger-300 bg-danger-50'}`}
        />
        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
      </div>

      {/* Only reachable if a name was stored before this control existed. */}
      {!valid && !open && (
        <p className="mt-1 text-[11px] text-danger-700">No ledger by this name — pick one, or create it.</p>
      )}

      {open && rect && (
        <div
          style={{
            position: 'fixed',
            top: rect.below ? rect.top : undefined,
            bottom: rect.below ? undefined : window.innerHeight - rect.top,
            left: rect.left,
            width: rect.width,
          }}
          className="z-[60] max-h-64 overflow-y-auto rounded-xl border border-ink-200 bg-white py-1 shadow-lg"
        >
          {matches.map((o, i) => (
            <button
              key={o.id}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o.name)}
              className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm ${i === active ? 'bg-ink-50' : ''}`}
            >
              <span className="min-w-0">
                <span className="block truncate text-ink-800">{o.name}</span>
                {o.parent && <span className="block truncate text-[11px] text-ink-400">{o.parent}</span>}
              </span>
              {o.name === value && <Check className="h-3.5 w-3.5 shrink-0 text-ink-500" />}
            </button>
          ))}

          {matches.length === 0 && !offerCreate && (
            <p className="px-3 py-2 text-xs text-ink-500">No ledgers yet.</p>
          )}

          {offerCreate && (
            <button
              type="button"
              onClick={() => setCreating(typed)}
              className="mt-1 flex w-full items-center gap-1.5 border-t border-ink-100 px-3 py-2 text-left text-sm font-medium text-inkblack"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">Create “{typed}” in the ledger master</span>
            </button>
          )}
        </div>
      )}

      {creating !== null && (
        <LedgerModal
          entityId={entityId}
          ledger={null}
          initialName={creating}
          onClose={() => setCreating(null)}
          onSaved={() => { const n = creating; setCreating(null); setOpen(false); setQuery(''); onCreated(n!); }}
        />
      )}
    </div>
  );
}
